import { useState } from "react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { MetaAdsInsightsPayload } from "@/types/marketing";

interface MetaAdsPanelProps {
  insights?: MetaAdsInsightsPayload | null;
  loading?: boolean;
  onBoost: (postId: string, budget: number) => Promise<void>;
  onCreateDarkPost: (payload: { caption: string; mediaUrl?: string | null; budget?: number }) => Promise<void>;
}

export function MetaAdsPanel({ insights, loading, onBoost, onCreateDarkPost }: MetaAdsPanelProps) {
  const [caption, setCaption] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [budget, setBudget] = useState(50);
  const [submitting, setSubmitting] = useState(false);
  const [boostBudget, setBoostBudget] = useState<Record<string, number>>({});

  const handleCreate = async () => {
    if (!caption.trim()) return;
    setSubmitting(true);
    try {
      await onCreateDarkPost({ caption, mediaUrl, budget });
      setCaption("");
      setMediaUrl("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBoostClick = async (postId: string) => {
    const rawBudget = boostBudget[postId] ?? insights?.insights?.find((insight) => insight.post_id === postId)?.recommended_budget ?? 50;
    await onBoost(postId, rawBudget);
  };

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Meta Ads</p>
          <p className="text-xs text-muted-foreground">Boost top clips or create dark posts.</p>
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-xs uppercase tracking-wide text-muted-foreground">Dark Post Caption</label>
        <Textarea
          rows={3}
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          className="bg-background/40 text-foreground"
          placeholder="Write the ad copy…"
        />
        <label className="text-xs uppercase tracking-wide text-muted-foreground">Media URL (optional)</label>
        <Input value={mediaUrl} onChange={(event) => setMediaUrl(event.target.value)} className="bg-background/40 text-foreground" />
        <label className="text-xs uppercase tracking-wide text-muted-foreground">Budget</label>
        <Input
          type="number"
          value={budget}
          min={10}
          onChange={(event) => setBudget(Number(event.target.value))}
          className="bg-background/40 text-foreground"
        />
        <Button onClick={handleCreate} disabled={submitting || !caption.trim()}>
          {submitting ? "Creating…" : "Create dark post"}
        </Button>
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Smart boost recommendations</p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading insights…</p>
        ) : (
          <div className="mt-2 space-y-3">
            {(insights?.insights ?? []).map((insight) => (
              <div key={insight.id} className="rounded-xl border border-border/70 bg-background/40 p-3 text-sm text-foreground/80">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground">Post {insight.post_id}</p>
                    <p className="text-xs text-muted-foreground">Urgency: {insight.urgency}</p>
                  </div>
                  <Button size="sm" onClick={() => void handleBoostClick(insight.post_id)}>
                    Boost
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Engagement {Math.round(insight.engagement_rate * 100)}%</p>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    type="number"
                    value={boostBudget[insight.post_id] ?? insight.recommended_budget}
                    onChange={(event) =>
                      setBoostBudget((prev) => ({ ...prev, [insight.post_id]: Number(event.target.value) }))
                    }
                    className="bg-background/40 text-foreground"
                  />
                  <span className="text-xs text-muted-foreground">USD</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Geo clusters: {insight.geo_clusters.join(", ")}</p>
              </div>
            ))}
            {!insights?.insights?.length ? <p className="text-xs text-muted-foreground">No recommendations yet.</p> : null}
          </div>
        )}
      </div>
    </Card>
  );
}
